import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import avatarImage from "../../Images/person_male.png";
import { HomeMessageService } from "../../Services/HomeMessageService";
import { NotificationService } from "../../Services/notificationService";
import { UserService } from "../../Services/userService";
import { NotificationRow } from "./notification";
import {
    normalizeNotificationNavigatePath,
    NotificationItem,
} from "./notificationUtils";

const PAGE_SIZE = 15;
const SCROLL_LOAD_THRESHOLD_PX = 120;

export interface HomeNotificationVirtualListProps {
    previousDays: number;
    reloadToken?: number;
    onTotalRecordsChange?: (total: number) => void;
    onNotificationsChange?: () => void;
    allowMarkUnread?: boolean;
}

const HomeNotificationVirtualList = ({
    previousDays,
    reloadToken = 0,
    onTotalRecordsChange,
    onNotificationsChange,
    allowMarkUnread = false,
}: HomeNotificationVirtualListProps) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const [items, setItems] = useState<NotificationItem[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [avatars, setAvatars] = useState<Record<string, string>>({});
    const avatarsRef = useRef(avatars);
    const scrollRef = useRef<HTMLDivElement>(null);
    const loadingRef = useRef(false);
    const nextPageRef = useRef(0);
    const hasMoreRef = useRef(true);
    avatarsRef.current = avatars;

    const loadNextPage = useCallback(async () => {
        if (loadingRef.current || !hasMoreRef.current) {
            return;
        }

        const page = nextPageRef.current;
        loadingRef.current = true;
        setLoading(true);

        try {
            const service = new HomeMessageService();
            const response = await service.fetchItems({
                previousDays,
                includeRead: true,
                page,
                size: PAGE_SIZE,
            });
            const pageData = response.data?.data;
            const content = (pageData?.content ?? []) as NotificationItem[];
            const total = pageData?.totalElements ?? 0;

            setItems((prev) => {
                const seen = new Set(prev.map((item) => item.uuid));
                const merged = [...prev];
                for (const item of content) {
                    if (!seen.has(item.uuid)) {
                        seen.add(item.uuid);
                        merged.push(item);
                    }
                }
                return merged;
            });

            setTotalRecords(total);
            onTotalRecordsChange?.(total);

            nextPageRef.current = page + 1;
            hasMoreRef.current = (page + 1) * PAGE_SIZE < total;
        } catch {
            hasMoreRef.current = false;
        } finally {
            loadingRef.current = false;
            setLoading(false);
        }
    }, [onTotalRecordsChange, previousDays]);

    useEffect(() => {
        nextPageRef.current = 0;
        hasMoreRef.current = true;
        setItems([]);
        setTotalRecords(0);
        onTotalRecordsChange?.(0);
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
        void loadNextPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when period/token changes only
    }, [previousDays, reloadToken]);

    const onScroll = useCallback(() => {
        const element = scrollRef.current;
        if (!element || loadingRef.current || !hasMoreRef.current) {
            return;
        }

        const distanceToBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
        if (distanceToBottom <= SCROLL_LOAD_THRESHOLD_PX) {
            void loadNextPage();
        }
    }, [loadNextPage]);

    const markAsRead = (uuid: string) => {
        setItems((prev) => prev.map((item) => (item.uuid === uuid ? { ...item, read: true } : item)));
        new NotificationService()
            .markRead(uuid)
            .then(() => onNotificationsChange?.())
            .catch(() =>
                setItems((prev) => prev.map((item) => (item.uuid === uuid ? { ...item, read: false } : item))),
            );
    };

    const markAsUnread = (uuid: string) => {
        setItems((prev) => prev.map((item) => (item.uuid === uuid ? { ...item, read: false } : item)));
        new NotificationService()
            .markUnread(uuid)
            .then(() => onNotificationsChange?.())
            .catch(() =>
                setItems((prev) => prev.map((item) => (item.uuid === uuid ? { ...item, read: true } : item))),
            );
    };

    const avatarUuids = useMemo(
        () =>
            [
                ...new Set(
                    items.map((n) => n.messageUser?.uuid).filter((uuid): uuid is string => Boolean(uuid)),
                ),
            ].sort().join(","),
        [items],
    );

    useEffect(() => {
        let cancelled = false;
        const uuids = avatarUuids ? avatarUuids.split(",") : [];
        const missing = uuids.filter((uuid) => !avatarsRef.current[uuid]);

        if (!missing.length) {
            return;
        }

        (async () => {
            const userService = new UserService();
            const pairs = await Promise.all(
                missing.map(async (uuid) => {
                    try {
                        return [uuid, await userService.fetchProfilePicture(uuid)] as const;
                    } catch {
                        return [uuid, avatarImage] as const;
                    }
                }),
            );
            if (cancelled) {
                return;
            }
            setAvatars((prev) => {
                const next = { ...prev };
                let changed = false;
                for (const [uuid, url] of pairs) {
                    if (!next[uuid]) {
                        next[uuid] = url;
                        changed = true;
                    }
                }
                return changed ? next : prev;
            });
        })();

        return () => {
            cancelled = true;
        };
    }, [avatarUuids]);

    if (!loading && totalRecords === 0 && items.length === 0) {
        return (
            <div className="dashly-notification-empty">
                <i className="pi pi-inbox" aria-hidden />
                <p className="dashly-notification-empty__title">{t("notification.empty.text")}</p>
                <p className="dashly-notification-empty__subtitle">{t("notification.empty.side.text")}</p>
            </div>
        );
    }

    return (
        <div className="dashly-notification-list dashly-notification-list--home-scroll">
            <div
                ref={scrollRef}
                className="dashly-notification-list__scroll"
                onScroll={onScroll}
            >
                {items.map((item) => (
                    <div key={item.uuid} className="dashly-notification-list__scroll-item">
                        <NotificationRow
                            notification={item}
                            avatar={
                                item.messageUser?.uuid
                                    ? avatars[item.messageUser.uuid] || avatarImage
                                    : undefined
                            }
                            expanded={expandedIds.has(item.uuid)}
                            onToggleExpand={() => {
                                setExpandedIds((current) => {
                                    const next = new Set(current);
                                    if (next.has(item.uuid)) {
                                        next.delete(item.uuid);
                                    } else {
                                        next.add(item.uuid);
                                    }
                                    return next;
                                });
                            }}
                            onMarkRead={markAsRead}
                            onMarkUnread={allowMarkUnread ? markAsUnread : undefined}
                            onNavigate={(path) => {
                                markAsRead(item.uuid);
                                navigate(normalizeNotificationNavigatePath(path) ?? path);
                            }}
                            translate={t}
                        />
                    </div>
                ))}
                {loading && (
                    <p className="dashly-notification-list__loading" aria-live="polite">
                        <i className="pi pi-spinner pi-spin" aria-hidden />
                        {t("common.action.loading")}
                    </p>
                )}
            </div>
        </div>
    );
};

export default HomeNotificationVirtualList;
